/* Pre-EAS Sprint — Deal-Share MVP.
 *
 * One button, two paths:
 *   1. `navigator.share` (mobile / Web Share API) → native share sheet
 *   2. fallback dialog (desktop) → WhatsApp / Telegram / e-mail / copy
 *
 * The share TEXT is built client-side from the localized `share`
 * namespace; the LINK is the server-rendered OG page
 * `${REACT_APP_BACKEND_URL}/api/d/{share_id}` so WhatsApp/iMessage/
 * Telegram crawlers see real Open-Graph metadata.
 * Market position is included ONLY when the backend graded it reliable
 * (`market_pct_below` — strict signal without a data caveat).
 */
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Copy, Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../api/client";
import { canUseNativeShare } from "../lib/shareCapability";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Share-payload cache + in-flight dedupe (module-level, per listing).
// Root-cause fix 2026-07-15: the POST /share ran on EVERY click BEFORE
// the sheet could open (first click = full API latency) — now repeat
// clicks are 0-request instant, and hover/focus prefetch warms the
// cache so the native sheet opens inside the user gesture.
const sharePayloadCache = new Map();
const shareInflight = new Map();

function fetchSharePayload(listingUlid) {
  if (sharePayloadCache.has(listingUlid)) {
    return Promise.resolve(sharePayloadCache.get(listingUlid));
  }
  if (shareInflight.has(listingUlid)) return shareInflight.get(listingUlid);
  const p = apiClient
    .post("/share", { listing_ulid: listingUlid }, { timeout: 8000 })
    .then((r) => {
      sharePayloadCache.set(listingUlid, r.data);
      return r.data;
    })
    .finally(() => shareInflight.delete(listingUlid));
  shareInflight.set(listingUlid, p);
  return p;
}

function buildShareText(t, data) {
  const emoji = data.vertical === "auto" ? "🚗" : "🏠";
  const lines = [`${emoji} ${t("found")}`, ""];
  if (data.title) lines.push(data.title);
  if (data.price_display) lines.push(data.price_display);
  if (data.market_pct_below) {
    lines.push(t("belowMarket", { pct: data.market_pct_below }));
  }
  if (data.ai_hint) {
    lines.push("", data.ai_hint);
  }
  lines.push("", `${t("viewDeal")}:`, `${BACKEND_URL}/api/d/${data.share_id}`);
  return lines.join("\n");
}

export const ShareDealButton = ({
  listingUlid,
  variant = "icon", // "icon" | "button"
  className = "",
  stopPropagation = false,
}) => {
  const { t } = useTranslation("share");
  const [busy, setBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [shareText, setShareText] = useState("");
  const shareIdRef = useRef(null);

  const trackSheet = (shareId) => {
    apiClient
      .post(`/share/public/${shareId}/event`, { type: "sheet_opened" })
      .catch(() => {});
  };

  const prefetch = () => {
    fetchSharePayload(listingUlid).catch(() => {});
  };

  const handleShare = async (e) => {
    if (stopPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (busy) return;
    let data;
    try {
      setBusy(true);
      data = await fetchSharePayload(listingUlid);
    } catch {
      toast.error(t("shareError"));
      return;
    } finally {
      // Root-cause fix: `busy` guards ONLY the API call. Previously it
      // was released after `await navigator.share()` — on Chrome
      // desktop that promise can NEVER settle when the OS share dialog
      // is dismissed without picking a target, so `busy` stayed true
      // forever and the button went permanently dead.
      setBusy(false);
    }
    shareIdRef.current = data.share_id;
    const text = buildShareText(t, data);
    setShareText(text);
    trackSheet(data.share_id);
    // Desktop → ALWAYS the in-app dialog (navigator.share is unreliable
    // on laptops: Windows flyout may silently never appear and the
    // promise never settles). Native sheet only on touch devices.
    if (canUseNativeShare()) {
      try {
        await navigator.share({ title: t("found"), text });
      } catch (err) {
        // AbortError: user closed the sheet. InvalidStateError: a share
        // is already in progress (double click) — neither is a failure.
        if (err?.name !== "AbortError" && err?.name !== "InvalidStateError") {
          setDialogOpen(true);
        }
      }
    } else {
      setDialogOpen(true);
    }
  };

  const encoded = encodeURIComponent(shareText);
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success(t("copied"));
    } catch {
      toast.error(t("shareError"));
    }
  };

  return (
    <>
      {variant === "button" ? (
        <button
          type="button"
          data-testid={`share-deal-btn-${listingUlid}`}
          onClick={handleShare}
          onMouseEnter={prefetch}
          onFocus={prefetch}
          onTouchStart={prefetch}
          className={`inline-flex items-center gap-2 rounded-full border
                      border-zinc-700 bg-zinc-900/80 px-4 py-2 text-sm
                      font-medium text-zinc-200 hover:text-amber-300
                      hover:border-amber-500/50 transition-colors ${className}`}
        >
          <Share2 className="h-4 w-4" />
          {t("shareDeal")}
        </button>
      ) : (
        <button
          type="button"
          data-testid={`share-deal-btn-${listingUlid}`}
          onClick={handleShare}
          onMouseEnter={prefetch}
          onFocus={prefetch}
          onTouchStart={prefetch}
          aria-label={t("shareDeal")}
          className={`inline-flex items-center justify-center rounded-full
                      border border-zinc-700 bg-zinc-900/80 text-zinc-300
                      hover:text-amber-300 hover:border-amber-500/50
                      transition-colors ${
                        variant === "iconSm" ? "h-7 w-7" : "h-9 w-9"
                      } ${className}`}
        >
          <Share2 className={variant === "iconSm" ? "h-3.5 w-3.5" : "h-4 w-4"} />
        </button>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          data-testid="share-fallback-dialog"
          aria-describedby={undefined}
          className="max-w-sm bg-zinc-950 border-zinc-800"
        >
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{t("shareVia")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <a
              data-testid="share-whatsapp"
              href={`https://wa.me/?text=${encoded}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2.5 text-sm text-zinc-200 hover:border-emerald-500/50"
            >
              <MessageCircle className="h-4 w-4 text-emerald-400" />
              {t("whatsapp")}
            </a>
            <a
              data-testid="share-telegram"
              href={`https://t.me/share/url?url=${encodeURIComponent(
                `${BACKEND_URL}/api/d/${shareIdRef.current || ""}`,
              )}&text=${encoded}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2.5 text-sm text-zinc-200 hover:border-sky-500/50"
            >
              <Send className="h-4 w-4 text-sky-400" />
              {t("telegram")}
            </a>
            <a
              data-testid="share-email"
              href={`mailto:?subject=${encodeURIComponent(t("emailSubject"))}&body=${encoded}`}
              className="flex items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2.5 text-sm text-zinc-200 hover:border-amber-500/50"
            >
              <Mail className="h-4 w-4 text-amber-400" />
              {t("email")}
            </a>
            <button
              type="button"
              data-testid="share-copy-btn"
              onClick={copyLink}
              className="flex items-center gap-2 rounded-lg border border-zinc-800 px-3 py-2.5 text-sm text-zinc-200 hover:border-zinc-500"
            >
              <Copy className="h-4 w-4 text-zinc-400" />
              {t("copyLink")}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
